import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  TopsIcon,
  OuterwearIcon,
  BottomsIcon,
  WorkwearIcon,
  HeadwearIcon,
  FootwearIcon,
  BagsIcon,
  AccessoriesIcon,
} from '../../shop/ProductTypeIcons';
import { TYPE_GROUPS } from '../../../data/clothing/browseConfig';

const GREEN = '#78BE20';

const ICONS: Record<string, React.FC<{ className?: string }>> = {
  tops: TopsIcon,
  outerwear: OuterwearIcon,
  bottoms: BottomsIcon,
  workwear: WorkwearIcon,
  headwear: HeadwearIcon,
  bags: BagsIcon,
  footwear: FootwearIcon,
  accessories: AccessoriesIcon,
};

interface CategoryIconBarProps {
  /** Product types currently selected, used to light up the matching group. */
  activeTypes: string[];
}

const CategoryIconBar: React.FC<CategoryIconBarProps> = ({ activeTypes }) => {
  const activeGroup = TYPE_GROUPS.find(
    (g) => activeTypes.length > 0 && activeTypes.every((t) => g.items.includes(t)),
  );

  // Phones: one slim scrolling row, small icon beside the label, underline along the bottom edge.
  // sm and up: the original stacked icon + label tiles.
  const cell =
    'group relative flex items-center justify-center gap-2 shrink-0 h-11 px-3 sm:h-auto sm:px-0 sm:flex-col sm:w-auto sm:flex-1 sm:py-3 transition-colors';
  const underline = 'block h-0.5 rounded-full transition-colors absolute bottom-0 inset-x-3 sm:static sm:inset-auto sm:w-6';
  const iconSize = 'w-5 h-5 sm:w-7 sm:h-7';

  // Keep the active category visible when the row scrolls sideways (phones only, in practice).
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const list = listRef.current;
    const el = list?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!list || !el || list.scrollWidth <= list.clientWidth) return;
    const l = list.getBoundingClientRect();
    const e = el.getBoundingClientRect();
    list.scrollTo({ left: list.scrollLeft + (e.left - l.left) - (l.width - e.width) / 2 });
  }, [activeGroup?.id, activeTypes.length]);

  return (
    <nav aria-label="Browse by category" className="border-y border-white/10 bg-black">
      <ul ref={listRef} className="max-w-[1500px] mx-auto flex sm:justify-between gap-1 px-2 sm:px-6 overflow-x-auto scrollbar-none">
        <li>
          <Link
            to="/clothing"
            aria-current={!activeTypes.length ? 'page' : undefined}
            className={`${cell} ${!activeTypes.length ? 'text-white' : 'text-white/55 hover:text-white'}`}
          >
            <span className={`grid place-items-center ${iconSize}`}>
              <span
                className="block w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-[3px] border-2"
                style={{ borderColor: !activeTypes.length ? GREEN : 'currentColor' }}
                aria-hidden
              />
            </span>
            <span className="font-poppins text-[11px] font-semibold tracking-[0.08em] uppercase whitespace-nowrap">
              All
            </span>
            <span
              className={underline}
              style={{ backgroundColor: !activeTypes.length ? GREEN : 'transparent' }}
              aria-hidden
            />
          </Link>
        </li>

        {TYPE_GROUPS.filter((g) => ICONS[g.id]).map((group) => {
          const Icon = ICONS[group.id];
          const active = activeGroup?.id === group.id;
          return (
            <li key={group.id}>
              <Link
                to={`/clothing?types=${group.items.map(encodeURIComponent).join(',')}`}
                aria-current={active ? 'page' : undefined}
                title={group.blurb}
                className={`${cell} ${active ? 'text-white' : 'text-white/55 hover:text-white'}`}
              >
                <Icon className={`${iconSize} transition-transform duration-300 group-hover:-translate-y-0.5`} />
                <span className="font-poppins text-[11px] font-semibold tracking-[0.08em] uppercase whitespace-nowrap">
                  {group.name}
                </span>
                <span
                  className={underline}
                  style={{ backgroundColor: active ? GREEN : 'transparent' }}
                  aria-hidden
                />
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default CategoryIconBar;
